const formatProductLevelData = data => data.map(d => ({
    ...d,
    date: new Date(d.timestamp),
    value: d.accumulatedConsumption,
}));

const formatFlowMeter = data => data.map(d => ({
    ...d,
    date: new Date(d.timestamp),
}));

const formattingSeries = (series, componentNameRef, lang) => series.map(({ analysisType: { name }, data, id, productMeter, flowMeter }, index) => ({
    id,
    name: `${name[lang]}-${componentNameRef[index]}`,
    data: (() => {
        if (!data.length) return [];
        if (productMeter) {
            return formatProductLevelData(data);
        }
        if (flowMeter) {
            return formatFlowMeter(data);
        }
        return data.map(d => ({
            ...d,
            date: new Date(d.measuredAt),
            value: +d.value,
        }));
    })(),
}));

const getSeriesData = async ($rq, siteId, graphicId, lang) => {
    const { series } = await $rq.getSeriesData(siteId, graphicId, {});
    const { data: { series: seriesBasedOnGraphic } } = await $rq.getSeries(siteId, graphicId);

    const componentNameArr = series.map(graphRef => {
        const graphicRef = seriesBasedOnGraphic.find(serieRef => serieRef.component.id === graphRef.component);
        return graphicRef ? graphicRef.component.name : '';
    });
    const formatted = formattingSeries(series, componentNameArr, lang);
    return formatted;
};

function canvasToBlobAsync(canvas, quality, type = 'image/png') {
    return new Promise(resolve => {
        canvas.toBlob(resolve, type, quality);
    });
}

function convertSvgElementToPngFile(svgElement, fileName = 'graph.png') {
    return new Promise((resolve, reject) => {
        // Get width and height from SVG attributes
        let width = svgElement.viewBox.baseVal.width || svgElement.width.baseVal.value || svgElement.getBoundingClientRect().width;
        let height = svgElement.viewBox.baseVal.height || svgElement.height.baseVal.value || svgElement.getBoundingClientRect().height;
        if (!width || !height) {
            width = 300;
            height = 150;
        }

        // Serialize SVG to string
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svgElement);

        // Create image and canvas
        const img = new window.Image();
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        img.onload = async () => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#fff'; // white background
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);

            const blob = await canvasToBlobAsync(canvas, 'image/png');
            URL.revokeObjectURL(url);
            if (blob) {
                const now = new Date();
                const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                const datedFileName = `${fileName}_${dateStr}.png`;
                const file = new File([blob], datedFileName, { type: 'image/png' });
                resolve(file);
            } else {
                reject(new Error('Failed to create PNG blob'));
            }
        };
        img.onerror = reject;
        img.src = url;
    });
}
async function getLegendMetadata($rq, series, locale, teamMainLang) {
    if (!series?.length) return [];
    return Promise.all(series.map(async (seriesInfo, index) => {
        if (!seriesInfo.component) {
            return {
                seriesInfo,
                unit: 'N/A',
                analysisName: `Line ${index + 1}`,
            };
        }
        // TODO: fix this seriesInfo.component._id when landed from Dashboard graphic and needs to send id
        // eslint-disable-next-line no-underscore-dangle
        const componentId = seriesInfo?.component?._id || seriesInfo?.component;
        const { data: { configurations } } = await $rq.getComponentConfigurations(componentId);
        const configuration = configurations.find(config => config?.analysisType.id === seriesInfo?.analysisType
            && config?.component?.id === seriesInfo?.component);
        const units = configuration?.productMeter?.reportUnits
            || configuration?.flowMeter?.unitsOfMeasure
            || configuration?.fuelMeter?.measurementUnit
            || (configuration?.analysisType?.measureUnit ? configuration?.analysisType?.measureUnit[locale] || configuration?.analysisType?.measureUnit[teamMainLang] : '')
            || configuration?.analysisType?.reportUnitOfMeasure;
        return {
            seriesInfo,
            unit: units || 'N/A',
            analysisName: configuration?.productMeter?.siteProduct?.product?.name
                || configuration?.flowMeter?.name
                || configuration?.fuelMeter?.fuelMeterName
                || configuration?.analysisType?.name?.[locale]
                || seriesInfo?.analysisName
                || seriesInfo?.analysisType?.name?.[locale]
                || `Line ${index + 1}`,
            color: seriesInfo.color,
        };
    }));
}

async function appendLegendToSVG($rq, clonedSvg, series, graphicName, locale, teamMainLang) {
    const ns = 'http://www.w3.org/2000/svg';

    // Find the actual SVG element if we're dealing with a wrapper
    let svgElement = clonedSvg;
    if (clonedSvg.nodeName !== 'svg') {
        // Try to find an SVG inside the cloned element
        const foundSvg = clonedSvg.querySelector('svg');
        if (foundSvg) {
            svgElement = foundSvg;
        }
    }

    // Get width - try different approaches
    let svgWidth;

    // Try direct attribute
    svgWidth = svgElement.getAttribute('width');

    // If not found, try from viewBox
    if (!svgWidth) {
        const viewBox = svgElement.getAttribute('viewBox');
        if (viewBox) {
            const [, , w] = viewBox.split(' ').map(Number);
            svgWidth = w;
        }
    }

    // If still not found, try from bounding client rect
    if (!svgWidth) {
        svgWidth = svgElement.getBoundingClientRect().width;
    }

    // Set a default if all else fails
    if (!svgWidth || svgWidth <= 0) {
        svgWidth = 800;
    }

    // Add graph title at the top
    if (graphicName) {
        const titleGroup = document.createElementNS(ns, 'g');
        titleGroup.setAttribute('class', 'graph-title');

        // Create title text with bottom margin and transparent background
        const titleText = document.createElementNS(ns, 'text');
        titleText.setAttribute('x', '50%');
        titleText.setAttribute('y', '20'); // Moved up to create space below
        titleText.setAttribute('text-anchor', 'middle');
        titleText.setAttribute('dominant-baseline', 'middle');
        titleText.setAttribute('font-size', '18px');
        titleText.setAttribute('font-weight', 'bold');
        titleText.setAttribute('margin-bottom', '20px'); // Not a standard SVG attribute but helps with clarity
        titleText.textContent = graphicName;
        titleGroup.appendChild(titleText);

        // Add a spacer line below the title to create margin
        const spacerLine = document.createElementNS(ns, 'line');
        spacerLine.setAttribute('x1', '0');
        spacerLine.setAttribute('x2', '0');
        spacerLine.setAttribute('y1', '35');
        spacerLine.setAttribute('y2', '35');
        spacerLine.setAttribute('stroke', 'transparent');
        spacerLine.setAttribute('stroke-width', '1');
        titleGroup.appendChild(spacerLine);

        svgElement.appendChild(titleGroup);

        // Adjust viewBox to make space for title if needed
        const viewBox = svgElement.getAttribute('viewBox');
        if (viewBox) {
            const [minX, minY, , height] = viewBox.split(' ').map(Number);
            svgElement.setAttribute('viewBox', `${minX} ${minY - 40} ${svgWidth} ${height + 40}`);
        }
    }

    const legendGroup = document.createElementNS(ns, 'g');
    legendGroup.setAttribute('class', 'legend');

    // Legend will be placed below the title at the top, so no extra width needed
    // Create a new container group to hold everything
    const containerGroup = document.createElementNS(ns, 'g');

    // Get reference to the title group if it exists
    const titleGroup = svgElement.querySelector('.graph-title');
    // Get original height before we start modifying the SVG
    let svgHeight;
    svgHeight = svgElement.getAttribute('height');

    if (!svgHeight) {
        const viewBox = svgElement.getAttribute('viewBox');
        if (viewBox) {
            const [, , , h] = viewBox.split(' ').map(Number);
            svgHeight = h;
        }
    }

    if (!svgHeight) {
        svgHeight = svgElement.getBoundingClientRect().height;
    }

    if (!svgHeight || svgHeight <= 0) {
        svgHeight = 600; // Default height
    }

    // First save all the original content - important to do this BEFORE removing anything
    const originalContent = [];
    Array.from(svgElement.childNodes).forEach(child => {
        // Skip the title group as we handle it separately
        if (child !== titleGroup) {
            originalContent.push(child.cloneNode(true));
        }
    });

    // Move original content to the container group
    originalContent.forEach(child => {
        containerGroup.appendChild(child);
    });

    // Clear the SVG while preserving its attributes
    while (svgElement.firstChild) {
        svgElement.removeChild(svgElement.firstChild);
    }

    // If there's a title group, add it back first
    if (titleGroup) {
        svgElement.appendChild(titleGroup);
    }

    // Calculate title and legend heights
    const titleHeight = 32; // px
    const legendMarginTop = 40; // px, space between title and legend
    const legendRows = Math.max(1, Math.ceil((series.length * 120) / (svgWidth - 40))); // rough estimate
    const legendHeight = legendRows * 24;
    // Reduce extra space after legend to bring graph closer
    const topOffset = titleHeight + legendMarginTop + legendHeight + 20; // 20px margin below legend

    // Move original content down by topOffset
    containerGroup.setAttribute('transform', `translate(0, ${topOffset})`);

    // Now add the container group with the original content
    svgElement.appendChild(containerGroup);

    // No extra width needed for legend on top we keep the original width
    svgElement.setAttribute('width', svgWidth);

    // Place legend below title, with margin
    const legendYOffset = titleHeight + legendMarginTop;
    legendGroup.setAttribute('transform', `translate(0, ${legendYOffset})`);

    // Update SVG height to fit everything
    svgHeight = Number(svgHeight) + topOffset;
    svgElement.setAttribute('height', svgHeight);
    // Update viewBox to fit new height
    const legendViewBox = svgElement.getAttribute('viewBox');
    if (legendViewBox) {
        const [minX, minY, vbWidth] = legendViewBox.split(' ').map(Number);
        svgElement.setAttribute('viewBox', `${minX} ${minY} ${vbWidth} ${svgHeight}`);
    } else {
        svgElement.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
        svgElement.setAttribute('preserveAspectRatio', 'xMinYMin meet');
    }

    // Layout legend items horizontally, wrap to new line if needed
    const maxLegendWidth = svgWidth - 40; // leave some margin
    const itemSpacing = 32; // horizontal gap between legend items (increased for extra padding)
    const rowHeight = 24; // vertical gap between rows (increased for clearer separation)

    // (Legend item creation/layout code block remains unchanged)

    // Now insert legendGroup as the first child after the title , otherwise as the very first child
    if (titleGroup && titleGroup.nextSibling) {
        svgElement.insertBefore(legendGroup, titleGroup.nextSibling);
    } else if (titleGroup) {
        svgElement.appendChild(legendGroup); // Only title exists, legend after
    } else {
        svgElement.insertBefore(legendGroup, svgElement.firstChild);
    }

    // measure text width using a canvas
    function measureTextWidth(text, font = '12px Arial') {
        const canvas = measureTextWidth.canvas || (measureTextWidth.canvas = document.createElement('canvas'));
        const context = canvas.getContext('2d');
        context.font = font;
        return context.measureText(text).width;
    }

    const legendData = await getLegendMetadata($rq, series, locale, teamMainLang);

    // calculate each legend item's width and label for accurate row packing
    const legendItemsData = legendData.map(data => {
        const label = `${data.analysisName} (${data.unit})`;
        const textWidth = measureTextWidth(label, '12px Arial');
        const itemWidth = 10 + 8 + textWidth + 10;
        return {
            itemWidth,
            label,
            color: data.color,
        };
    });

    // pack items into rows (RTL)
    const rows = [];
    let currentRow = [];
    let currentRowWidth = 0;
    legendItemsData.forEach(item => {
        const additionalWidth = item.itemWidth + (currentRow.length > 0 ? itemSpacing : 0);
        if (currentRowWidth + additionalWidth > maxLegendWidth && currentRow.length > 0) {
            rows.push(currentRow);
            currentRow = [];
            currentRowWidth = 0;
        }
        currentRow.push(item);
        currentRowWidth += additionalWidth;
    });
    if (currentRow.length > 0) rows.push(currentRow);

    // Render rows from top to bottom, items in each row from right to left
    rows.forEach((row, rowIdx) => {
        let currentX = maxLegendWidth;
        for (let i = row.length - 1; i >= 0; i -= 1) {
            const item = row[i];
            currentX -= item.itemWidth;
            const legendItem = document.createElementNS(ns, 'g');
            legendItem.setAttribute('transform', `translate(${currentX}, ${rowIdx * rowHeight})`);

            const rect = document.createElementNS(ns, 'rect');
            rect.setAttribute('x', '0');
            rect.setAttribute('y', '-10');
            rect.setAttribute('width', '10');
            rect.setAttribute('height', '10');
            rect.setAttribute('fill', item.color);
            legendItem.appendChild(rect);

            const text = document.createElementNS(ns, 'text');
            text.setAttribute('x', '20');
            text.setAttribute('y', '0');
            text.setAttribute('font-size', '12px');
            text.setAttribute('dominant-baseline', 'middle');
            text.textContent = item.label;
            legendItem.appendChild(text);
            legendGroup.appendChild(legendItem);
            currentX -= itemSpacing;
        }
    });


    // Insert legendGroup as the first child after the title (if it exists), otherwise as the very first child
    const maybeTitle = svgElement.querySelector('.graph-title');
    if (maybeTitle && maybeTitle.nextSibling) {
        svgElement.insertBefore(legendGroup, maybeTitle.nextSibling);
    } else if (maybeTitle) {
        svgElement.appendChild(legendGroup); // Only title exists, legend after
    } else {
        svgElement.insertBefore(legendGroup, svgElement.firstChild);
    }
    return svgElement;
}

export {
    getSeriesData,
    convertSvgElementToPngFile,
    appendLegendToSVG,
    getLegendMetadata,
};
